/**
 * InstrumentSelector Component
 *
 * Modal dialog for selecting an instrument from search results.
 * Used when automatic resolution fails or returns ambiguous results.
 *
 * T058: Instrument selection modal for manual instrument resolution
 * NOW THEME-AWARE
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  FlatList,
  ActivityIndicator,
  TextInput,
} from 'react-native';
import { financialDataAPI } from '../../services/api/financialDataApi';
import { getToken } from '../../services/tokenStorage';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';

interface InstrumentCandidate {
  id: number;
  symbol: string;
  company_name?: string;
  exchange?: string;
  currency: string;
}

interface InstrumentSelectorProps {
  visible: boolean;
  symbol: string;
  currency: string;
  initialCandidates?: InstrumentCandidate[];
  onClose: () => void;
  onSelect: (instrument: InstrumentCandidate) => void;
}

export function InstrumentSelector({
  visible,
  symbol,
  currency,
  initialCandidates = [],
  onClose,
  onSelect,
}: InstrumentSelectorProps) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  // Ensure symbol is a string
  const safeSymbol = symbol ? String(symbol) : '';

  const [searchQuery, setSearchQuery] = useState(safeSymbol);
  const [searchResults, setSearchResults] = useState<InstrumentCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const financialDataApi = financialDataAPI;

  // Initialize with initial candidates when modal opens
  useEffect(() => {
    if (visible) {
      setSearchQuery(safeSymbol);
      setSearched(false);

      // If initial candidates are provided, use them directly
      if (initialCandidates && initialCandidates.length > 0) {
        setSearchResults(initialCandidates);
        setSearched(true);
      } else {
        // No candidates provided, clear results and trigger search
        setSearchResults([]);
        // Small delay to ensure state is updated
        setTimeout(() => {
          handleSearch(safeSymbol);
        }, 100);
      }
    }
  }, [visible, initialCandidates, safeSymbol]);

  const handleSearch = async (queryOverride?: string) => {
    const queryToSearch = queryOverride || searchQuery;

    console.log('handleSearch called:', {
      queryOverride,
      searchQuery,
      queryToSearch,
      type: typeof queryToSearch,
    });

    // Ensure queryToSearch is a string before calling trim
    if (!queryToSearch || typeof queryToSearch !== 'string' || !queryToSearch.trim()) {
      console.log('Search skipped: empty or invalid query');
      return;
    }

    console.log('Starting search for:', queryToSearch.trim());
    setLoading(true);
    setSearched(false);

    try {
      // Get authentication token
      const token = await getToken();
      if (!token) {
        console.error('Authentication required');
        setSearchResults([]);
        setSearched(true);
        setLoading(false);
        return;
      }

      console.log('Token acquired, calling resolveInstrument API...');
      // Call the resolve API to get candidates
      const response = await financialDataApi.resolveInstrument(
        queryToSearch.trim(),
        currency,
        token
      );

      console.log('API response received:', response);

      if (response.status === 'ambiguous' && response.candidates) {
        setSearchResults(response.candidates);
      } else if (response.status === 'matched' && response.instrument_id) {
        // Single match - add as single candidate
        setSearchResults([
          {
            id: response.instrument_id,
            symbol: response.normalized_symbol,
            currency: currency,
          },
        ]);
      } else {
        // No matches - empty results
        setSearchResults([]);
      }

      setSearched(true);
    } catch (error: any) {
      console.error('Search failed:', error);
      setSearchResults([]);
      setSearched(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (instrument: InstrumentCandidate) => {
    onSelect(instrument);
    onClose();
  };

  return (
    <Modal visible={visible} animationType="slide" transparent={true} onRequestClose={onClose}>
      <View style={styles.modalContainer}>
        <View style={[styles.modalContent, { backgroundColor: theme.colors.surface }]}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={[styles.title, { color: theme.colors.text }]}>
              {t('portfolio.selectInstrument', { defaultValue: 'Select Instrument' })}
            </Text>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <Text style={[styles.closeButtonText, { color: theme.colors.textSecondary }]}>✕</Text>
            </TouchableOpacity>
          </View>

          {/* Search Input */}
          <View style={styles.searchContainer}>
            <TextInput
              style={[
                styles.searchInput,
                {
                  backgroundColor: theme.colors.backgroundElevated,
                  borderColor: theme.colors.border,
                  color: theme.colors.text,
                },
              ]}
              value={searchQuery}
              onChangeText={setSearchQuery}
              placeholder={t('portfolio.enterSymbolOrCompanyName', {
                defaultValue: 'Enter symbol or company name',
              })}
              placeholderTextColor={theme.colors.textTertiary}
              autoCapitalize="characters"
              onSubmitEditing={handleSearch}
              returnKeyType="search"
            />
            <TouchableOpacity
              style={[styles.searchButton, { backgroundColor: theme.colors.primary }]}
              onPress={() => {
                console.log('Search button pressed, searchQuery:', searchQuery);
                handleSearch();
              }}
              disabled={loading}
            >
              {loading ? (
                <ActivityIndicator size="small" color={theme.colors.textInverse} />
              ) : (
                <Text style={[styles.searchButtonText, { color: theme.colors.textInverse }]}>
                  {t('portfolio.search', { defaultValue: 'Search' })}
                </Text>
              )}
            </TouchableOpacity>
          </View>

          {/* Search Results */}
          <View style={styles.resultsContainer}>
            {loading && (
              <View style={styles.centerContainer}>
                <ActivityIndicator size="large" color={theme.colors.primary} />
                <Text style={[styles.loadingText, { color: theme.colors.textSecondary }]}>
                  {t('portfolio.searching', { defaultValue: 'Searching...' })}
                </Text>
              </View>
            )}

            {!loading && searched && searchResults.length === 0 && (
              <View style={styles.centerContainer}>
                <Text style={[styles.noResultsText, { color: theme.colors.text }]}>
                  {t('portfolio.noInstrumentsFound', { defaultValue: 'No instruments found' })}
                </Text>
                <Text style={[styles.noResultsSubtext, { color: theme.colors.textSecondary }]}>
                  {t('portfolio.trySearchSymbolOrCompany', {
                    defaultValue: 'Try searching for the symbol or company name',
                  })}
                </Text>
              </View>
            )}

            {!loading && searched && searchResults.length > 0 && (
              <FlatList
                data={searchResults}
                keyExtractor={(item) => item.symbol}
                renderItem={({ item }) => (
                  <TouchableOpacity
                    style={[
                      styles.resultItem,
                      {
                        backgroundColor: theme.colors.backgroundElevated,
                        borderColor: theme.colors.border,
                      },
                    ]}
                    onPress={() => handleSelect(item)}
                  >
                    <View style={styles.resultHeader}>
                      <Text style={[styles.resultSymbol, { color: theme.colors.text }]}>
                        {item.symbol}
                      </Text>
                      <View
                        style={[styles.currencyBadge, { backgroundColor: theme.colors.border }]}
                      >
                        <Text style={[styles.currencyText, { color: theme.colors.textSecondary }]}>
                          {item.currency}
                        </Text>
                      </View>
                    </View>
                    {item.company_name && (
                      <Text style={[styles.resultName, { color: theme.colors.text }]}>
                        {item.company_name}
                      </Text>
                    )}
                    {item.exchange && (
                      <Text style={[styles.resultExchange, { color: theme.colors.textSecondary }]}>
                        {item.exchange}
                      </Text>
                    )}
                  </TouchableOpacity>
                )}
                style={styles.resultsList}
              />
            )}

            {!loading && !searched && (
              <View style={styles.centerContainer}>
                <Text style={[styles.promptText, { color: theme.colors.text }]}>
                  {t('portfolio.enterSymbolToSearch', {
                    defaultValue: 'Enter a symbol to search for instruments',
                  })}
                </Text>
                <Text style={[styles.promptSubtext, { color: theme.colors.textSecondary }]}>
                  {t('portfolio.searchBySymbolOrCompany', {
                    defaultValue: 'You can search by symbol (e.g., AAPL) or company name',
                  })}
                </Text>
              </View>
            )}
          </View>

          {/* Cancel Button */}
          <TouchableOpacity
            style={[styles.cancelButton, { backgroundColor: theme.colors.backgroundElevated }]}
            onPress={onClose}
          >
            <Text style={[styles.cancelButtonText, { color: theme.colors.text }]}>
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
}

/**
 * Create themed styles dynamically
 */
function createThemedStyles(theme: any) {
  return StyleSheet.create({
    modalContainer: {
      flex: 1,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      justifyContent: 'center',
      alignItems: 'center',
    },
    modalContent: {
      width: '90%',
      maxWidth: 500,
      maxHeight: '80%',
      borderRadius: theme.radii.lg,
      padding: theme.spacing.lg,
      borderWidth: theme.id === 'bauhaus' ? 2 : 0,
      borderColor: theme.colors.border,
    },
    header: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: theme.spacing.lg,
    },
    title: {
      fontSize: theme.typography.fontSizeXl,
      fontWeight: theme.typography.fontWeightBold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    closeButton: {
      padding: theme.spacing.xs,
    },
    closeButtonText: {
      fontSize: theme.typography.fontSize3Xl,
      fontWeight: theme.typography.fontWeightLight,
    },
    searchContainer: {
      flexDirection: 'row',
      marginBottom: theme.spacing.md,
      gap: theme.spacing.sm,
    },
    searchInput: {
      flex: 1,
      height: 44,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderRadius: theme.radii.md,
      paddingHorizontal: theme.spacing.md,
      fontSize: theme.typography.fontSizeMd,
    },
    searchButton: {
      paddingHorizontal: theme.spacing.md,
      borderRadius: theme.radii.md,
      justifyContent: 'center',
      minWidth: 80,
      alignItems: 'center',
    },
    searchButtonText: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    resultsContainer: {
      flex: 1,
      minHeight: 200,
      maxHeight: 400,
      marginBottom: theme.spacing.md,
    },
    resultsList: {
      flex: 1,
    },
    resultItem: {
      padding: theme.spacing.md,
      marginBottom: theme.spacing.sm,
      borderRadius: theme.radii.md,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
    },
    resultHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: theme.spacing.xs,
    },
    resultSymbol: {
      fontSize: theme.typography.fontSizeLg,
      fontWeight: theme.typography.fontWeightBold,
    },
    currencyBadge: {
      paddingHorizontal: theme.spacing.sm,
      paddingVertical: theme.spacing.xs,
      borderRadius: theme.radii.full,
    },
    currencyText: {
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    resultName: {
      fontSize: theme.typography.fontSizeSm,
      marginBottom: 2,
    },
    resultExchange: {
      fontSize: theme.typography.fontSizeXs,
    },
    centerContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      paddingVertical: 32,
    },
    loadingText: {
      marginTop: theme.spacing.md,
      fontSize: theme.typography.fontSizeMd,
    },
    noResultsText: {
      fontSize: theme.typography.fontSizeLg,
      fontWeight: theme.typography.fontWeightSemibold,
      marginBottom: theme.spacing.sm,
    },
    noResultsSubtext: {
      fontSize: theme.typography.fontSizeSm,
      textAlign: 'center',
    },
    promptText: {
      fontSize: theme.typography.fontSizeMd,
      marginBottom: theme.spacing.sm,
      textAlign: 'center',
    },
    promptSubtext: {
      fontSize: theme.typography.fontSizeSm,
      textAlign: 'center',
    },
    cancelButton: {
      paddingVertical: theme.spacing.md,
      borderRadius: theme.radii.md,
      alignItems: 'center',
    },
    cancelButtonText: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
  });
}
